import React, { useState } from 'react';
import { Building2, Check } from 'lucide-react';
import { AppSidebar } from './AppSidebar';
import { TopBar } from './TopBar';
import { StagingBanner } from './StagingBanner';
import { MobileBottomNav } from './MobileBottomNav';
import { ViewAsOrgBanner } from '@/components/ui';
import { useAuth } from '@/contexts/AuthContext';
import { SidebarProvider, SidebarInset } from '@/components/ui/sidebar';
import { ChatWidget } from '@/components/ai-chat';
import { SimCheckChatWidget } from '@/components/ai-chat/SimCheckChatWidget';
import { NotificationToastProvider } from '@/components/notifications/NotificationToastProvider';
import { useIsMobile } from '@/hooks/use-mobile';
import { useEntitlements } from '@/hooks/useEntitlements';

// The Claude chat (aiChatStream) is the default. The legacy Gemini ChatWidget only when explicitly
// VITE_USE_NEW_CHAT=false: its MCP /api/chat has no GEMINI_API_KEY and fails every message.
const USE_NEW_CHAT = import.meta.env.VITE_USE_NEW_CHAT !== 'false';

const ROLE_STYLES: Record<string, { bg: string; border: string; role: string }> = {
  owner:  { bg: 'bg-accent-orange/10', border: 'border-accent-orange/30', role: 'text-accent-orange' },
  admin:  { bg: 'bg-accent-blue/10',   border: 'border-accent-blue/30',   role: 'text-accent-blue' },
  member: { bg: 'bg-accent-green/10',  border: 'border-accent-green/30',  role: 'text-accent-green' },
  viewer: { bg: 'bg-muted',            border: 'border-border',           role: 'text-muted-foreground' },
};

export function AppShell({ children }: { children: React.ReactNode }) {
  const {
    isViewingAsOrg, viewedAsOrg, stopViewingAsOrg,
    needsOrgSelection, organizations, switchOrganization,
    currentUser, activeOrganization,
  } = useAuth();
  const isMobile = useIsMobile();
  const { entitlements } = useEntitlements();
  // Gate the AI assistant on the plan entitlement. Stays mounted while
  // entitlements load (permissive defaults) and only hides when a plan
  // explicitly disables it.
  const aiAssistantAllowed = entitlements.aiAssistant !== false;

  const [switching, setSwitching] = useState<string | null>(null);

  const handlePickOrg = async (orgId: string) => {
    setSwitching(orgId);
    try {
      await switchOrganization(orgId);
    } finally {
      setSwitching(null);
    }
  };

  return (
    <>
      <SidebarProvider defaultOpen={!isMobile}>
        <AppSidebar />
        <SidebarInset>
          <StagingBanner />
          <TopBar />
          {isViewingAsOrg && viewedAsOrg && (
            <ViewAsOrgBanner
              organization={viewedAsOrg}
              onStopViewing={stopViewingAsOrg}
            />
          )}
          <main className="min-h-[calc(100vh-4rem)] p-4 pb-24 sm:p-6 md:pb-20">
            {needsOrgSelection ? null : children}
          </main>
        </SidebarInset>
      </SidebarProvider>
      <MobileBottomNav />

      {/* Realtime → toast bridge (critical/warning only); renders nothing.
          Keyed on user+org so an identity switch remounts the bridge and
          re-baselines its "seen" set against the new feed — otherwise every
          unread critical/warning in the new org's snapshot would toast at
          once on org switch. */}
      <NotificationToastProvider
        key={`${currentUser?.uid ?? ''}:${activeOrganization?.id ?? ''}`}
      />

      {/* Org picker — shown when user belongs to multiple orgs and has no saved preference */}
      {needsOrgSelection && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
          <div className="w-full max-w-sm bg-card border border-border rounded-xl shadow-2xl overflow-hidden">
            <div className="p-6 border-b border-border">
              <div className="flex items-center gap-3 mb-1">
                <div className="flex size-9 items-center justify-center rounded-lg bg-sidebar-primary shrink-0">
                  <Building2 className="size-5 text-sidebar-primary-foreground" />
                </div>
                <div>
                  <h2 className="font-semibold text-foreground">Choose an organization</h2>
                  <p className="text-xs text-muted-foreground">You belong to multiple organizations</p>
                </div>
              </div>
            </div>
            <div className="p-3 space-y-1 max-h-72 overflow-y-auto">
              {organizations.map((org) => {
                const s = ROLE_STYLES[org.role] ?? ROLE_STYLES.viewer;
                const isLoading = switching === org.organizationId;
                return (
                  <button
                    key={org.organizationId}
                    onClick={() => handlePickOrg(org.organizationId)}
                    disabled={switching !== null}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border text-left transition-colors disabled:opacity-60 ${s.bg} ${s.border} hover:opacity-90`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-foreground text-sm truncate">{org.organizationName}</p>
                      <p className={`text-xs font-medium capitalize ${s.role}`}>{org.role}</p>
                    </div>
                    {isLoading ? (
                      <div className="size-4 border-2 border-current border-t-transparent rounded-full animate-spin shrink-0 text-muted-foreground" />
                    ) : (
                      <Check className="size-4 text-muted-foreground/30 shrink-0" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* AI Chat Widget - desktop only; the floating FAB would cover the mobile bottom nav.
          Gated on the plan's aiAssistant entitlement (permissive while loading). */}
      {!isMobile && aiAssistantAllowed && (USE_NEW_CHAT ? <SimCheckChatWidget /> : <ChatWidget />)}
    </>
  );
}




